import React, { useState } from 'react';
import { IAssistantMessage, IQuestionOption } from '../../../types';
import { ChatMessage } from '../../message-box';
import { MultipleChoiceCard } from './MultipleChoiceCard';
import { ConfirmUpdateModal } from '../../ConfirmUpdateModal';

interface SelectMultipleMessageBublesProps {
    message: IAssistantMessage;
    handleSelect: (questionId: string, options: IQuestionOption[]) => void;
    updateSelect: (questionId: string, options: IQuestionOption[]) => void;
    isProcessing?: boolean;
    isEditMode?: boolean; // Flag to disable confirmation modal in edit mode
}

export const SelectMultipleMessageBubles: React.FC<SelectMultipleMessageBublesProps> = ({
    message,
    handleSelect: handleSelectProp,
    updateSelect: updateSelectProp,
    isProcessing = false,
    isEditMode = false,
}) => {
    const [showModal, setShowModal] = useState(false);
    const [pendingOptions, setPendingOptions] = useState<IQuestionOption[]>([]);

    // Check if this question has already been answered
    const hasAnswer = message.response?.answer && message.response.answer.length > 0;

    const handleSelect = (options: IQuestionOption[]) => {
        if (hasAnswer && !isEditMode) {
            // Show confirmation modal before updating (only if NOT in edit mode)
            setPendingOptions(options);
            setShowModal(true);
        } else if (hasAnswer && isEditMode) {
            // In edit mode, update directly without confirmation
            updateSelectProp(message.question!.questionId, options);
        } else {
            // New answer, proceed directly
            handleSelectProp(message.question!.questionId, options);
        }
    };

    const handleConfirm = () => {
        if (pendingOptions.length > 0) {
            updateSelectProp(message.question!.questionId, pendingOptions);
        }
        setShowModal(false);
        setPendingOptions([]);
    };

    const handleCancel = () => {
        setShowModal(false);
        setPendingOptions([]);
    };
    // Get old answer text for modal (join multiple options)
    const oldAnswerText = message.response?.answer?.map(opt => opt.optionText).join(', ') || '';
    const newAnswerText = pendingOptions.map(opt => opt.optionText).join(', ') || '';

    return (
        <div>
            <ChatMessage message={message} />
            <MultipleChoiceCard
                options={message.question!.options || []}
                selectedOptions={message.response?.answer || []}
                onSelect={handleSelect}
                isDisabled={isProcessing}
            />
            <ConfirmUpdateModal
                visible={showModal}
                questionText={message.question!.question}
                oldAnswer={oldAnswerText}
                newAnswer={newAnswerText}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
            />
        </div>
    );
}
